import React from "react";
import { Mic, Square, BarChart2 } from "lucide-react";
import { SendIcon } from "./icons";
import PulseIndicator from "../../PulseIndicator/PulseIndicator";

interface InputAreaProps {
  inputValue: string;
  onInputChange: (value: string) => void;
  onSendMessage: () => void;
  onStopQuery: () => void;
  isLoading: boolean;
  isSearching: boolean;
  isVoiceMode: boolean;
  isVoiceRecording: boolean;
  isTranscribing: boolean;
  isChartMode: boolean;
  onToggleChartMode: () => void;
  onVoiceRecordingClick: () => void;
  error: string | null;
  clearError: () => void;
  transcriptionError: string | null;
  voiceRecorderError: string | null;
  inputRef?: React.Ref<HTMLInputElement>;
}

const InputArea: React.FC<InputAreaProps> = ({
  inputValue,
  onInputChange,
  inputRef,
  onSendMessage,
  onStopQuery,
  isLoading,
  isSearching,
  isVoiceMode,
  isVoiceRecording,
  isTranscribing,
  isChartMode,
  onToggleChartMode,
  onVoiceRecordingClick,
  error,
  clearError,
  transcriptionError,
  voiceRecorderError,
}) => {
  return (
    <div className="p-3">
      <div className="relative flex items-center">
        <button
          onClick={onToggleChartMode}
          className={`absolute left-2 p-1.5 rounded-md transition-all duration-200 ${
            isChartMode
              ? "bg-blue-100 text-blue-600 shadow-inner"
              : "text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          }`}
          title="Include Chart"
        >
          <BarChart2 size={18} />
        </button>

        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => {
            onInputChange(e.target.value);
            if (error) {
              clearError();
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              onSendMessage();
            }
          }}
          placeholder="How can I help you today?"
          disabled={isLoading}
          className="w-full pl-11 pr-24 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-100 focus:border-orange-300 text-sm text-slate-700 shadow-sm placeholder:text-slate-400"
        />

        <div className="absolute right-2 flex items-center gap-1">
          {isVoiceMode && (
            <button
              onClick={onVoiceRecordingClick}
              disabled={isLoading || isTranscribing}
              className={`p-2 rounded-lg transition ${
                isVoiceRecording || isTranscribing
                  ? "bg-red-500 text-white"
                  : "text-slate-400 hover:bg-white hover:shadow-sm hover:text-slate-600"
              } disabled:opacity-50 disabled:cursor-not-allowed`}
              title={
                isVoiceRecording
                  ? "Stop recording"
                  : isTranscribing
                  ? "Transcribing..."
                  : "Start voice recording"
              }
            >
              {isVoiceRecording ? (
                <PulseIndicator size="small" color="#ffffff" />
              ) : isTranscribing ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <Mic size={18} />
              )}
            </button>
          )}
          {isLoading || isSearching ? (
            <button
              onClick={onStopQuery}
              className="p-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition shadow-sm"
              title="Stop query"
            >
              <Square size={18} fill="currentColor" />
            </button>
          ) : (
            <button
              onClick={onSendMessage}
              disabled={!inputValue.trim()}
              className={`p-2 rounded-lg transition shadow-sm ${
                inputValue.trim()
                  ? "bg-orange-500 text-white hover:bg-orange-600 shadow-orange-200"
                  : "bg-orange-200 text-white cursor-default"
              }`}
              title="Send message"
            >
              <SendIcon />
            </button>
          )}
        </div>
      </div>

      {(transcriptionError || voiceRecorderError) && (
        <div className="mt-2 px-1">
          <p className="text-xs text-red-600">
            {transcriptionError || voiceRecorderError}
          </p>
        </div>
      )}
    </div>
  );
};

export default InputArea;
